// ============================================
// fetchWithRetry — resilient fetch wrapper
// Automatically retries on:
//   - Network errors (TypeError: Failed to fetch)
//   - HTTP 502 / 503 / 504 (server/gateway errors)
// Stops retrying on 4xx (client errors — retrying won't help)
// ============================================

/**
 * @param {string} url
 * @param {RequestInit} options
 * @param {object} retryOpts
 * @param {number} retryOpts.retries - max attempts (default 3)
 * @param {number} retryOpts.delayMs - base delay between attempts, doubled each retry (default 500)
 * @returns {Promise<Response>}
 */
export const fetchWithRetry = async (url, options = {}, retryOpts = {}) => {
  const retries = retryOpts.retries ?? 3;
  const baseDelay = retryOpts.delayMs ?? 500;

  let lastError;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res = await fetch(url, options);
      // Retry on transient server errors
      if (res.status === 502 || res.status === 503 || res.status === 504) {
        lastError = new Error(`HTTP ${res.status}`);
        if (attempt < retries - 1) {
          const wait = baseDelay * Math.pow(2, attempt); // exponential backoff: 500, 1000, 2000
          // Small random jitter so retry storms don't sync
          const jitter = Math.random() * 200;
          await sleep(wait + jitter);
          continue;
        }
      }
      // Return the response — caller decides how to handle non-retriable errors
      return res;
    } catch (err) {
      lastError = err;
      // Network error (TypeError: Failed to fetch, connection refused, etc.)
      if (attempt < retries - 1) {
        const wait = baseDelay * Math.pow(2, attempt);
        const jitter = Math.random() * 200;
        await sleep(wait + jitter);
        continue;
      }
    }
  }
  throw lastError || new Error('fetchWithRetry exhausted all retries');
};

/**
 * Convenience wrapper that also parses JSON
 */
export const fetchJsonWithRetry = async (url, options = {}, retryOpts = {}) => {
  const res = await fetchWithRetry(url, options, retryOpts);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return res.json();
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));
